import { Link } from "@/components/flexkit/Link";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useAnimatedUnmount } from "@/hooks/use-animated-unmount";
import type { WithTrackingId } from "@/types/tracking";
import type { ReactNode } from "react";
import { forwardRef } from "react";
import type { To } from "react-router-dom";
import {
    iconContainerVariants,
    iconVariants,
    labelVariants,
    sidebarButtonVariants,
    TRANSITION_DURATION,
} from "@/components/SidebarButton.variants";
import { NotificationDot } from "@/components/flexkit/badge/NotificationDot";

/**
 * Common props shared across all SidebarButton variants.
 * Available regardless of which variant is being used.
 */
type CommonProps = {
    /** Optional unique identifier for the button */
    id?: string;

    /** Primary icon displayed on the left side of the button */
    icon?: ReactNode;

    /** Optional secondary icon displayed on the right side of the button */
    iconRight?: ReactNode;

    /** Text label for the button. Used for both display and accessibility */
    label: string;

    /** Indicates if this button represents the current page or selected item */
    active?: boolean;

    /** Additional CSS classes to apply to the button */
    className?: string;

    /** Whether to show a notification badge on the button */
    showBadge?: boolean;

    /**
     * Whether to hide the text label, showing only the icon.
     * When true, the label is still used for accessibility via aria-label
     */
    hideLabel?: boolean;

    /**
     * Explicitly set the button variant. If not provided, the variant is
     * automatically determined from the presence of href, to, or onClick props
     */
    variant?: "link" | "route" | "button";
};

/**
 * External link variant of SidebarButton.
 * Used for linking to external URLs outside the application.
 * Selected automatically when `href` is provided.
 * When target="_blank" is used, rel="noopener noreferrer" is automatically added for security.
 *
 * ```tsx
 * <SidebarButton
 *   href="https://docs.example.com"
 *   target="_blank"
 *   icon={<IconHelp />}
 *   label="Documentation"
 * />
 * ```
 */
type LinkButtonProps = CommonProps & {
    href: string;
    target?: string;
    to?: never;
    onClick?: never;
    type?: never;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps | "href" | "target">;

/**
 * Internal route variant of SidebarButton.
 * Used for navigation within the application using React Router.
 * Selected automatically when `to` is provided.
 * Uses React Router's Link component internally for client-side navigation.
 * Automatically handles active state styling when the current route matches.
 *
 * ```tsx
 * <SidebarButton
 *   to="/settings"
 *   icon={<IconSettings />}
 *   label="Settings"
 *   active={isSettingsPage}
 * />
 * ```
 */
type RouteButtonProps = CommonProps & {
    to: To;
    href?: never;
    target?: never;
    onClick?: never;
    type?: never;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps | "href" | "target">;

/**
 * Action button variant of SidebarButton.
 * Used for handling click events and form submissions.
 * Selected automatically when neither href nor to is provided.
 * Renders as a native <button> element for better accessibility.
 * Type defaults to "button" to prevent accidental form submissions.
 * When hideLabel is true, the label is still used for aria-label.
 *
 * ```tsx
 * <SidebarButton
 *   onClick={handleToggleSidebar}
 *   icon={<IconToggle />}
 *   label="Toggle Sidebar"
 *   hideLabel={true}
 *   type="button"
 * />
 * ```
 */
type ActionButtonProps = CommonProps & {
    onClick?: () => void;
    type?: "button" | "submit" | "reset";
    href?: never;
    to?: never;
    target?: never;
} & WithTrackingId & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps | "onClick" | "type">;

/**
 * Union type combining all possible SidebarButton variants.
 * Variant is determined by the presence of specific props:
 * - href → link variant (external)
 * - to → route variant (internal)
 * - onClick/neither → button variant
 */
type SidebarButtonProps = LinkButtonProps | RouteButtonProps | ActionButtonProps;

/**
 * A versatile sidebar button component that adapts its behavior based on provided props.
 * Automatically determines its variant based on the presence of specific props:
 * - `href` → external link variant
 * - `to` → internal route variant (React Router)
 * - `onClick` or neither → button variant
 *
 * The variant can be explicitly overridden using the `variant` prop if needed.
 *
 * Accessibility features:
 * - Uses semantic HTML elements (<a> for links, <button> for actions)
 * - Maintains proper focus management
 * - Provides aria-current for active routes
 * - Preserves aria-label when label is visually hidden
 * - Marks decorative elements as aria-hidden
 *
 * Animation features:
 * - Smooth transitions for label visibility
 * - Hardware-accelerated transforms
 * - Configurable animation duration
 *
 * Forwards ref to the underlying <a> or <button> element.
 *
 * ```tsx
 * // External link variant
 * <SidebarButton
 *   href="https://docs.example.com"
 *   icon={<IconDocs />}
 *   label="Documentation"
 * />
 *
 * // Internal route variant
 * <SidebarButton
 *   to="/settings"
 *   icon={<IconSettings />}
 *   label="Settings"
 *   active={isSettingsPage}
 * />
 *
 * // Action button variant
 * <SidebarButton
 *   onClick={handleAction}
 *   icon={<IconAction />}
 *   label="Perform Action"
 *   hideLabel={true}
 * />
 * ```
 */
export const SidebarButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, SidebarButtonProps>(
    ({ id, icon, iconRight, label, className, active, variant, showBadge, hideLabel, ...props }, ref) => {
        const { shouldRender: shouldRenderLabel } = useAnimatedUnmount({
            duration: TRANSITION_DURATION,
            isHidden: hideLabel ?? false,
        });

        // Infer variant from props if not explicitly set
        const effectiveVariant = variant || ("href" in props ? "link" : "to" in props ? "route" : "button");

        // Smart detection: only render icon container if we actually have an icon
        const hasIcon = icon !== undefined;

        const iconContainerClasses = iconContainerVariants({
            design: "new",
        });

        const iconClasses = iconVariants({
            design: "new",
        });

        const labelClasses = cn(
            labelVariants({
                design: "new",
                visibility: hideLabel ? "hidden" : "visible",
            }),
            // Adjust padding based on icon presence
            hasIcon ? "px-0" : "px-2",
            className,
        );

        const content = (
            <div className="relative flex w-full flex-row items-center">
                {/* Only render icon container when we actually have an icon */}
                {hasIcon && (
                    <div className={iconContainerClasses} data-state={hideLabel ? "collapsed" : "default"}>
                        <span
                            className={iconClasses}
                            // Icon is always decorative - the button's accessible name comes from
                            // either the visible label or the aria-label attribute
                            aria-hidden="true"
                        >
                            {icon}
                        </span>
                        {/* Badge can appear regardless of icon presence */}
                        {showBadge && <NotificationDot className="right-1 -top-1 mt-1" />}
                    </div>
                )}
                {shouldRenderLabel && <div className={labelClasses}>{label}</div>}
                {iconRight && (
                    <div className="flex items-center px-2" aria-hidden="true">
                        {iconRight}
                    </div>
                )}
            </div>
        );

        const clazzes = cn(
            sidebarButtonVariants({
                design: "new",
                state: active ? "active" : "default",
            }),
            className,
        );

        // Helper to wrap element with tooltip when label is hidden
        const wrapWithTooltip = (element: React.ReactElement) => {
            if (hideLabel) {
                return (
                    <Tooltip content={label} side="right" usePortal>
                        {element}
                    </Tooltip>
                );
            }
            return element;
        };

        // External link
        if (effectiveVariant === "link") {
            const { href, target, ...restProps } = props as LinkButtonProps;
            return wrapWithTooltip(
                <a
                    id={id}
                    className={clazzes}
                    href={href}
                    target={target}
                    rel={target === "_blank" ? "noopener noreferrer" : undefined}
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    aria-current={active ? "page" : undefined}
                    aria-label={hideLabel ? label : undefined}
                    {...restProps}
                >
                    {content}
                </a>,
            );
        }

        // Internal route
        if (effectiveVariant === "route") {
            const { to, ...restProps } = props as RouteButtonProps;
            return wrapWithTooltip(
                <Link
                    id={id}
                    className={clazzes}
                    to={to}
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    aria-current={active ? "page" : undefined}
                    aria-label={hideLabel ? label : undefined}
                    {...restProps}
                >
                    {content}
                </Link>,
            );
        }

        // Button (default)
        const { onClick, type = "button", ...restProps } = props as ActionButtonProps;
        return wrapWithTooltip(
            <button
                id={id}
                type={type}
                className={clazzes}
                onClick={onClick}
                ref={ref as React.Ref<HTMLButtonElement>}
                aria-label={hideLabel ? label : undefined}
                {...restProps}
                data-tracking-id={restProps["data-tracking-id"]}
            >
                {content}
            </button>,
        );
    },
);

SidebarButton.displayName = "SidebarButton";
